// Libraries
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { useState } from 'react';
// Components
import { EyeIcon, EyeIconType } from '@atoms/index';
// Types
import { FormInputProps } from './FormInput.type';
// Utils
import text from '@translations/en.json';

/**
 * Represents a form input component.
 *
 * @component
 * @param {FormInputProps} props - The props for the FormInput component.
 * @param {string} props.label - The label text for the form input.
 * @param {boolean} props.wasSubmitted - Indicates whether the form containing the input was submitted.
 * @param {string} [props.error] - An optional error message associated with the input.
 * @param {boolean} [props.isPassword=false] - Indicates whether the input is a password field.
 * @param {boolean} [props.isRequired=false] - Indicates whether the input is required.
 * @param {boolean} [props.isHidden=false] - Indicates whether the input is hidden.
 * @returns {ReactElement} The FormInput component.
 */
export const FormInput = (props: FormInputProps) => {
  const {
    label,
    units,
    wasSubmitted = true,
    error,
    isPassword = false,
    isRequired = false,
    isHidden = false,
    value,
    ...rest
  } = props;
  const [showPassword, setShowPassword] = useState(isPassword);
  const isInvalid = wasSubmitted && !!error;
  const inputType = showPassword ? 'password' : 'text';
  const iconType = showPassword ? EyeIconType.CLOSE : EyeIconType.OPEN;
  const inputGroupFontFamily = showPassword && value ? 'Verdana' : 'Jost';
  const inputGroupMb = isInvalid ? 0 : 8;
  const inputShadow = isInvalid ? 'errorOutline' : 'outline';
  const showEyeIcon = isPassword && value;
  const eyeIconAriaLabel = showPassword ? text.hide_password : text.show_password;
  const unitsStyles = !!units
    ? {
        position: 'relative',
        _after: {
          content: `"${units}"`,
          position: 'absolute',
          fontSize: 'm',
          top: 1.5,
          right: 1.5,
          color: 'text.faded',
        },
      }
    : {};

  return (
    <>
      {isHidden ? (
        <>
          <label hidden htmlFor={props.id}>
            {label}
          </label>
          <input hidden autoComplete={props.id} type='text' id={props.id} name={props.name} defaultValue={value} />
        </>
      ) : (
        <FormControl isRequired={isRequired} isInvalid={isInvalid} hidden={isHidden}>
          <FormLabel mb={1} mr={0} fontSize='s' fontWeight='normal' color='text.faded'>
            {label}
          </FormLabel>
          <InputGroup color='text.dark' fontFamily={inputGroupFontFamily} mb={inputGroupMb} sx={unitsStyles}>
            <Input
              value={value}
              h={8}
              fontSize='m'
              type={inputType}
              isInvalid={isInvalid}
              errorBorderColor='text.error'
              sx={{
                '::-ms-reveal': {
                  display: 'none',
                },
              }}
              _focus={{
                borderColor: 'transparent',
                shadow: inputShadow,
              }}
              {...rest}
            />
            {showEyeIcon && (
              <InputRightElement h={8}>
                <IconButton
                  w={6}
                  h={6}
                  minW={6}
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={eyeIconAriaLabel}
                  colorScheme='inputScheme'
                  icon={<EyeIcon iconType={iconType} color='text.faded' />}
                />
              </InputRightElement>
            )}
          </InputGroup>
          {isInvalid && (
            <FormErrorMessage h={7} mt={1} fontSize='xs' color='text.error'>
              {error}
            </FormErrorMessage>
          )}
        </FormControl>
      )}
    </>
  );
};
